/**
 * Pagination control. Works in zero-based page indices (matching the backend),
 * but renders one-based labels for humans. Collapses long ranges with ellipses
 * so the control stays compact no matter how many pages there are.
 */
interface PaginationProps {
  /** Zero-based current page. */
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

/**
 * Build the list of page indices (and "…" gaps) to show. Always includes the
 * first and last page, plus a window around the current page.
 */
function buildRange(page: number, total: number): (number | "gap")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i);
  }
  const out: (number | "gap")[] = [0];
  const start = Math.max(1, page - 1);
  const end = Math.min(total - 2, page + 1);

  if (start > 1) out.push("gap");
  for (let i = start; i <= end; i++) out.push(i);
  if (end < total - 2) out.push("gap");

  out.push(total - 1);
  return out;
}

export function Pagination({ page, totalPages, onChange }: PaginationProps) {
  if (totalPages <= 1) return null;
  const range = buildRange(page, totalPages);

  return (
    <nav className="pagination" aria-label="Pagination">
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page === 0}
        aria-label="Previous page"
      >
        ‹ Prev
      </button>

      {range.map((item, i) =>
        item === "gap" ? (
          <span key={`gap-${i}`} aria-hidden="true" style={{ color: "var(--ink-500)" }}>
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            data-current={item === page}
            aria-current={item === page ? "page" : undefined}
            onClick={() => onChange(item)}
          >
            {item + 1}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages - 1}
        aria-label="Next page"
      >
        Next ›
      </button>
    </nav>
  );
}
